import React, { createContext, useState, useEffect, useContext } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Super admin has all permissions
    if (user.role === 'super-admin') return true;
    
    // Check permissions array from backend
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions.includes(permission);
    }
    
    return false;
  };

  // Check if user has any of the given permissions
  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    if (user.role === 'super-admin') return true;
    
    if (user.permissions && Array.isArray(user.permissions)) {
      return permissions.some(permission => user.permissions.includes(permission));
    }
    
    return false;
  };

  // Check if user has all of the given permissions
  const hasAllPermissions = (permissions) => {
    if (!user) return false;
    if (user.role === 'super-admin') return true;
    
    if (user.permissions && Array.isArray(user.permissions)) {
      return permissions.every(permission => user.permissions.includes(permission));
    }
    
    return false;
  };

  const isAdmin = () => {
    return user?.role === 'admin' || 
           user?.role === 'super-admin' || 
           user?.role?.includes('-admin');
  };

  const isSuperAdmin = () => {
    return user?.role === 'super-admin';
  };

  const isStateAdmin = () => {
    return user?.role === 'state-admin';
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
    }
    setLoading(false);
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpires');
    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { 
      ...user, 
      ...updatedUserData
    };
    localStorage.setItem('userData', JSON.stringify(newUserData));
    setUser(newUserData);
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
    isAdmin: isAdmin(),
    isSuperAdmin: isSuperAdmin(),
    isStateAdmin: isStateAdmin(),
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };